<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="3" rounded="xxl">
            <v-card-title>
              <h3
                class="font-weight-bold"
                v-text="'Account Delete Requests'"
              ></h3>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search by name or phone..."
                    color="primary"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  >
                  </v-text-field>
                </v-col>
                <v-col md="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.status"
                    :items="statusOptions"
                    item-text="text"
                    item-value="value"
                    label="Status"
                    clearable
                    @click:clear="(filter.status = null), (page = 1), getData()"
                    @input="(page = 1), getData()"
                    prepend-inner-icon="mdi-filter"
                  ></v-autocomplete>
                </v-col>
                <v-col md="3" class="d-flex justify-end">
                  <v-btn
                    color="primary"
                    rounded
                    @click="$router.push('/account-delete-requests/create')"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Add Request
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="12">
          <v-card elevation="3" rounded="xxl">
            <v-card-text>
              <section
                v-if="isLoading"
                class="d-flex align-center justify-center"
                style="height: 400px"
              >
                <health-loader />
              </section>
              <v-data-table
                v-else
                :headers="headers"
                :items="accountDeleteRequests"
                :page="page"
                :items-per-page="rowsPerPage"
                hide-default-footer
                class="elevation-1"
                :no-data-text="'No account delete requests found'"
              >
                <template v-slot:[`item.status`]="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    text-color="white"
                    small
                  >
                    {{ getStatusText(item.status) }}
                  </v-chip>
                </template>
                <template v-slot:[`item.created_at`]="{ item }">
                  {{ formatDate(item.created_at) }}
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="info"
                        v-bind="attrs"
                        v-on="on"
                        @click="viewDetails(item)"
                      >
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>View Details</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="success"
                        v-bind="attrs"
                        v-on="on"
                        @click="editRequest(item)"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>
                  <v-tooltip bottom v-if="!item.is_deleted">
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                        @click="deleteRequest(item)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                  <v-tooltip bottom v-else>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="warning"
                        v-bind="attrs"
                        v-on="on"
                        @click="restoreRequest(item)"
                      >
                        <v-icon>mdi-restore</v-icon>
                      </v-btn>
                    </template>
                    <span>Restore</span>
                  </v-tooltip>
                </template>
              </v-data-table>
              <div class="text-center pt-2">
                <v-pagination
                  v-model="page"
                  :length="paginationLength"
                  rounded
                ></v-pagination>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="successDialog.show" max-width="400" persistent>
        <v-card rounded="xxl">
          <v-card-title class="text-h6 text-center">
            <v-icon large color="success" class="mr-2">mdi-check-circle</v-icon>
            Success!
          </v-card-title>
          <v-card-text class="text-center">
            {{ successDialog.message }}
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-btn color="success" @click="closeSuccessDialog">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Confirm Delete Dialog -->
      <v-dialog v-model="deleteDialog.show" max-width="500" persistent>
        <v-card rounded="xxl">
          <v-card-title class="text-h6">
            <v-icon color="error" class="mr-2">mdi-delete</v-icon>
            Confirm Delete
          </v-card-title>
          <v-card-text>
            Are you sure you want to delete this account delete request for <strong>{{ deleteDialog.item?.name }}</strong>?
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn color="grey" text @click="deleteDialog.show = false">Cancel</v-btn>
            <v-btn color="error" @click="confirmDelete" :loading="deleteDialog.loading">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "AccountDeleteRequestsIndex",
  data() {
    return {
      filter: {
        search_keyword: "",
        status: null,
      },
      accountDeleteRequests: [],
      page: 1,
      rowsPerPage: 10,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      headers: [
        { text: "ID", value: "id", sortable: false },
        { text: "Name", value: "name", sortable: false },
        { text: "Phone", value: "phone", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Created At", value: "created_at", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
      statusOptions: [
        { text: "Pending", value: "1" },
        { text: "Complete", value: "2" },
      ],
      successDialog: {
        show: false,
        message: "",
      },
      deleteDialog: {
        show: false,
        item: null,
        loading: false,
      },
    };
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {
          page: this.page,
          rowsPerPage: this.rowsPerPage,
          search_keyword: this.filter.search_keyword || "",
          status: this.filter.status || "",
        };

        const query = Object.keys(params)
          .map(key => `${key}=${encodeURIComponent(params[key])}`)
          .join("&");

        const response = await axios.get(`account_delete_request?${query}`);
        
        this.accountDeleteRequests = response.data.data;
        this.count = response.data.count;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.error("Error fetching account delete requests:", error);
        this.$store.dispatch("errors/setError", "Failed to fetch account delete requests");
      } finally {
        this.isLoading = false;
      }
    },
    
    getStatusText(status) {
      const statusMap = {
        "1": "Pending",
        "2": "Complete",
      };
      return statusMap[status] || "Unknown";
    },
    
    getStatusColor(status) {
      const colorMap = {
        "1": "warning",
        "2": "success",
      };
      return colorMap[status] || "grey";
    },
    
    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    
    viewDetails(item) {
      this.$router.push(`/account-delete-requests/${item.id}`);
    },
    
    editRequest(item) {
      this.$router.push(`/account-delete-requests/${item.id}/edit`);
    },
    
    deleteRequest(item) {
      this.deleteDialog.item = item;
      this.deleteDialog.show = true;
    },
    
    async confirmDelete() {
      this.deleteDialog.loading = true;
      try {
        await axios.post(`account_delete_request/delete/${this.deleteDialog.item.id}`);
        this.successDialog.message = "Account delete request deleted successfully!";
        this.successDialog.show = true;
        this.deleteDialog.show = false;
        this.getData();
      } catch (error) {
        console.error("Error deleting request:", error);
        this.$store.dispatch("errors/setError", "Failed to delete request");
      } finally {
        this.deleteDialog.loading = false;
      }
    },
    
    async restoreRequest(item) {
      try {
        await axios.post(`account_delete_request/restore/${item.id}`);
        this.successDialog.message = "Account delete request restored successfully!";
        this.successDialog.show = true;
        this.getData();
      } catch (error) {
        console.error("Error restoring request:", error);
        this.$store.dispatch("errors/setError", "Failed to restore request");
      }
    },
    
    closeSuccessDialog() {
      this.successDialog.show = false;
      this.successDialog.message = "";
    },
  },
};
</script>

<style scoped>
.striped-table tbody tr:nth-child(odd) {
  background-color: #f5f5f5;
}
</style>